import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ClockCounterClockwise, Plus, PencilLine, Trash, Eye,
  ArrowClockwise, Funnel, User
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { getActivityLogs, getActivityStats } from '../services/api';
import { PageHero } from '../components/motion/PageHero';

const MODULE_LABELS = {
  contacts: 'Contacts', organizations: 'Organisations', opportunities: 'Opportunites',
  dossiers: 'Dossiers', documents: 'Documents', payments: 'Paiements',
  tasks: 'Taches', partners: 'Partenaires', reporting: 'Reporting', settings: 'Parametres',
};

const ACTION_ICONS = {
  create: { icon: Plus, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Creation' },
  update: { icon: PencilLine, color: 'text-[#E8A365]', bg: 'bg-amber-50', label: 'Modification' },
  delete: { icon: Trash, color: 'text-red-500', bg: 'bg-red-50', label: 'Suppression' },
  read: { icon: Eye, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Consultation' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()} ${hours}:${mins}`;
}

export function ActivityLogPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ module: '', action: '', limit: 50, skip: 0 });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.module) params.module = filters.module;
      if (filters.action) params.action = filters.action;
      params.limit = filters.limit;
      params.skip = filters.skip;
      const [logsRes, statsRes] = await Promise.all([
        getActivityLogs(params),
        getActivityStats()
      ]);
      setLogs(logsRes.data.items);
      setTotal(logsRes.data.total);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="activity-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Journal d'activité"
        subtitle="Traçabilité et audit des actions utilisateurs"
        icon={<ClockCounterClockwise size={24} weight="duotone" className="text-white" />}
        gradient="from-[#5E6D66] to-[#2D5A4C]"
        accents={['#FF8E00', '#00AEEF']}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Journal d'activite
          </h1>
          <p className="text-[#5E6D66] mt-1">{total} actions enregistrees</p>
        </div>
        <Button variant="outline" onClick={fetchLogs} className="border-[#E8E6E1]" data-testid="refresh-logs">
          <ArrowClockwise size={16} className="mr-1.5" /> Actualiser
        </Button>
      </div>

      {/* Stats cards */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.by_action.map(({ action, count }) => {
            const cfg = ACTION_ICONS[action] || ACTION_ICONS.read;
            const Icon = cfg.icon;
            return (
              <Card key={action} className="border-[#E8E6E1]" data-testid={`stat-${action}`}>
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                    <Icon size={16} className={cfg.color} weight="bold" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#1A2421]">{count}</p>
                    <p className="text-[10px] text-[#5E6D66] uppercase">{cfg.label}s</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-[#5E6D66]">
          <Funnel size={14} />
          <span className="text-xs font-medium">Filtres:</span>
        </div>
        <Select value={filters.module} onValueChange={(v) => setFilters(p => ({ ...p, module: v === 'all' ? '' : v, skip: 0 }))}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filter-module"><SelectValue placeholder="Tous les modules" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les modules</SelectItem>
            {Object.entries(MODULE_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.action} onValueChange={(v) => setFilters(p => ({ ...p, action: v === 'all' ? '' : v, skip: 0 }))}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-action"><SelectValue placeholder="Toutes actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes actions</SelectItem>
            {Object.entries(ACTION_ICONS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" />
        </div>
      ) : logs.length === 0 ? (
        <Card className="border-[#E8E6E1]">
          <CardContent className="py-12 text-center">
            <ClockCounterClockwise size={40} className="mx-auto text-[#E8E6E1] mb-3" />
            <p className="text-[#5E6D66]">Aucune activite enregistree</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="activity-timeline">
          {logs.map((log, i) => {
            const cfg = ACTION_ICONS[log.action] || ACTION_ICONS.read;
            const Icon = cfg.icon;
            return (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-[#E8E6E1] hover:border-[#8DB0A1]/40 transition-colors bg-white" data-testid={`log-${i}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                  <Icon size={14} className={cfg.color} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-[#1A2421]">{log.user_name}</span>
                    <Badge variant="secondary" className="text-[10px]">{cfg.label}</Badge>
                    <Badge variant="outline" className="text-[10px] border-[#E8E6E1]">{MODULE_LABELS[log.module] || log.module}</Badge>
                  </div>
                  {log.entity_label && (
                    <p className="text-xs text-[#5E6D66] mt-0.5">{log.entity_label}</p>
                  )}
                  {log.detail && (
                    <p className="text-xs text-[#5E6D66] mt-0.5">{log.detail}</p>
                  )}
                  {/* Changes diff */}
                  {log.changes && Object.keys(log.changes).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {Object.entries(log.changes).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-2 text-[11px]">
                          <span className="text-[#5E6D66] font-medium">{key}:</span>
                          {val.avant !== undefined && val.avant !== null && (
                            <span className="text-red-400 line-through">{String(val.avant).substring(0, 60)}</span>
                          )}
                          <span className="text-[#5E6D66]">&rarr;</span>
                          <span className="text-emerald-600 font-medium">{String(val.apres).substring(0, 60)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <span className="text-[10px] text-[#8DB0A1] flex-shrink-0 whitespace-nowrap">{formatDate(log.timestamp)}</span>
              </div>
            );
          })}

          {/* Pagination */}
          {total > filters.limit && (
            <div className="flex items-center justify-center gap-2 pt-3">
              <Button
                variant="outline"
                size="sm"
                disabled={filters.skip === 0}
                onClick={() => setFilters(p => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))}
              >
                Precedent
              </Button>
              <span className="text-xs text-[#5E6D66]">{filters.skip + 1}-{Math.min(filters.skip + filters.limit, total)} sur {total}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={filters.skip + filters.limit >= total}
                onClick={() => setFilters(p => ({ ...p, skip: p.skip + p.limit }))}
              >
                Suivant
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
